import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fmtErr, money, fdate } from "../api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import MapView from "../components/MapView";
import InfoTip from "../components/InfoTip";
import FleetSection from "../components/FleetSection";
import JobInvoices from "../components/JobInvoices";
import BookPage from "./BookPage";
import AccountPage from "./AccountPage";
import { TeamTab, MetricsTab } from "./admin/CompanyTabs2";
import { DeliveriesTab, ProvidersTab, ReportsTab, AddressBookTab } from "./admin/CompanyTabs3";
import { Stat, StatusPill } from "../components/bits";
import { Plus, AlertTriangle, ArrowRight, LayoutDashboard, History, Truck, Receipt, Users, BookUser, BarChart3, FileText, Handshake, Container, Code2, Settings } from "lucide-react";

const inputCls = "cr-input";

const NAV = [
  ["Work", [
    ["today", "Today", LayoutDashboard],
    ["deliveries", "Delivery history", History],
    ["drivers", "Drivers", Truck],
  ]],
  ["Workspace", [
    ["invoices", "Invoices", Receipt],
    ["team", "Team management", Users],
    ["addresses", "Address book", BookUser],
    ["metrics", "Booking metrics", BarChart3],
    ["reports", "Reports", FileText],
    ["providers", "Providers", Handshake],
    ["fleet", "Fleet", Container],
    ["api", "API & webhooks", Code2],
  ]],
];

export default function CompanyDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("today");
  const [d, setD] = useState(null);
  const [docsReq, setDocsReq] = useState(null);
  const [keys, setKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [newHook, setNewHook] = useState("");

  const load = useCallback(() => {
    api.get("/company/dashboard").then((r) => setD(r.data)).catch(() => {});
    api.get("/documents/required").then((r) => setDocsReq(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  useEffect(() => {
    if (tab === "api") {
      api.get("/apikeys").then((r) => setKeys(r.data.keys)).catch(() => {});
      api.get("/webhooks").then((r) => setWebhooks(r.data.webhooks)).catch(() => {});
    }
  }, [tab]);

  if (!user) return null;
  if (user.status !== "approved") {
    return (
      <div className="max-w-md mx-auto px-4 py-20 text-center" data-testid="company-pending">
        <Seo title="Account pending | CourierRadar" noindex />
        <h1 className="text-2xl font-bold">Awaiting approval</h1>
        <p className="text-sm text-zinc-600 mt-3">We verify every company before it can post jobs. You'll be notified by email.</p>
      </div>
    );
  }

  const acceptBestBid = async (bid) => {
    try { await api.post(`/bids/${bid.id}/accept`); toast.success(`Assigned to ${bid.courier_name}`); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-8" data-testid="company-dashboard">
      <Seo title="Company dashboard | CourierRadar" noindex />
      <div className="grid lg:grid-cols-[230px_minmax(0,1fr)] gap-6 items-start">

        <aside className="lg:sticky lg:top-20 lg:self-start lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto cr-scroll" data-testid="company-sidebar">
          <div className="mb-4 px-1">
            <p className="text-[10px] uppercase tracking-widest text-[#A09D95] font-semibold">Company</p>
            <h1 className="text-lg font-extrabold tracking-tight truncate">{user.company_name || user.name}</h1>
          </div>
          <button onClick={() => setTab("book")} data-testid="new-job-btn" className={`w-full flex items-center justify-center gap-1.5 mb-5 transition-all ${tab === "book" ? "cr-btn-dark" : "cr-btn-dark"}`}>
            <Plus className="w-4 h-4" /> New delivery
          </button>
          <nav className="flex lg:flex-col gap-1 overflow-x-auto pb-1">
            {NAV.map(([group, items]) => (
              <div key={group} className="contents lg:block">
                <p className="hidden lg:block cr-label !mb-1.5 mt-4 first:mt-0 px-3">{group}</p>
                {items.map(([id, label, Icon]) => (
                  <button key={id} data-testid={`company-tab-${id}`} onClick={() => setTab(id)}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap lg:w-full text-left transition-all ${tab === id ? "bg-[#1C1B1A] text-white" : "text-[#78756E] hover:text-[#1C1B1A] hover:bg-[#F6F3EA]"}`}>
                    <Icon className="w-3.5 h-3.5 shrink-0" /> {label}
                  </button>
                ))}
              </div>
            ))}
            <div className="contents lg:block">
              <p className="hidden lg:block cr-label !mb-1.5 mt-4 px-3">Account</p>
              <button onClick={() => setTab("account")} data-testid="company-tab-account"
                className={`flex items-center gap-2.5 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap lg:w-full text-left transition-all ${tab === "account" ? "bg-[#1C1B1A] text-white" : "text-[#78756E] hover:text-[#1C1B1A] hover:bg-[#F6F3EA]"}`}>
                <Settings className="w-3.5 h-3.5 shrink-0" /> Account settings
              </button>
            </div>
          </nav>
        </aside>

        <main className="min-w-0">
          {docsReq && !docsReq.complete && (
            <Link to="/account" data-testid="compliance-banner" className="block bg-[#1C1B1A] text-white rounded-[28px] p-4 mb-6 hover:bg-[#2C2B2A] transition-colors">
              <p className="text-sm font-bold">Company documents required</p>
              <p className="text-xs text-white/60 mt-0.5">{docsReq.required.filter((r) => !r.verified).map((r) => r.doc_type.replace(/_/g, " ")).join(" · ")} → upload in your account</p>
            </Link>
          )}

          {tab === "today" && d && (
            <div data-testid="company-today">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="company-kpis">
                <Stat label="Unassigned" value={d.counts.unassigned} />
                <Stat label="Bids to decide" value={d.counts.bids} />
                <Stat label="In progress" value={d.counts.active} />
                <Stat label="At risk" value={d.counts.at_risk} />
              </div>
              <div className="grid lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                  <section data-testid="bids-to-decide">
                    <h2 className="text-sm font-bold mb-3">Bids to decide ({d.bids_to_decide.length})</h2>
                    {d.bids_to_decide.length === 0 && <p className="text-xs text-zinc-400 border border-dashed border-zinc-200 rounded-xl p-5 text-center">No pending bids.</p>}
                    <div className="space-y-2.5">
                      {d.bids_to_decide.map(({ job, bids }) => (
                        <div key={job.id} data-testid={`bid-decision-${job.ref}`} className="cr-card !p-3.5">
                          <div className="flex items-center justify-between">
                            <button onClick={() => navigate(`/jobs/${job.id}`)} className="text-left">
                              <p className="font-mono text-[11px] text-zinc-400">{job.ref}</p>
                              <p className="text-sm font-semibold">{job.pickup?.city} → {job.delivery?.city}</p>
                            </button>
                            <span className="text-xs text-zinc-500">{bids.length} bid{bids.length > 1 ? "s" : ""}</span>
                          </div>
                          <div className="flex flex-wrap items-center justify-between gap-2 mt-2.5 border-t border-zinc-50 pt-2.5">
                            <p className="text-xs">Best: <strong>{bids[0].courier_name}</strong> · {money(bids[0].amount, bids[0].currency)}
                              {bids[0].courier_rating ? ` · ★${bids[0].courier_rating}` : ""}</p>
                            <div className="flex gap-2">
                              <button data-testid={`quick-accept-${job.ref}`} onClick={() => acceptBestBid(bids[0])}
                                className="bg-[#1C1B1A] text-white text-xs font-semibold px-3.5 py-2 rounded-full hover:bg-[#2C2B2A] transition-colors">Accept best</button>
                              <button data-testid={`compare-${job.ref}`} onClick={() => navigate(`/jobs/${job.id}`)}
                                className="border border-zinc-200 text-xs font-semibold px-3.5 py-2 rounded-lg hover:bg-zinc-50">Compare</button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  <section data-testid="unassigned-jobs">
                    <h2 className="text-sm font-bold mb-3 flex items-center">Unassigned jobs ({d.unassigned.length})<InfoTip text="Open jobs without a courier. Jobs with pickup today need dispatch now." /></h2>
                    {d.unassigned.length === 0 && <p className="text-xs text-zinc-400 border border-dashed border-zinc-200 rounded-xl p-5 text-center">All jobs are assigned.</p>}
                    <div className="space-y-2.5">
                      {d.unassigned.map((j) => (
                        <div key={j.id} data-testid={`unassigned-${j.ref}`} className="cr-card !p-3.5 flex flex-wrap items-center justify-between gap-2">
                          <button onClick={() => navigate(`/jobs/${j.id}`)} className="text-left min-w-0">
                            <p className="font-mono text-[11px] text-zinc-400">{j.ref}</p>
                            <p className="text-sm font-semibold truncate">{j.pickup?.city} → {j.delivery?.city}
                              {j.pickup_date && <span className="font-normal text-zinc-500"> · {fdate(j.pickup_date)}</span>}</p>
                          </button>
                          <div className="flex gap-2">
                            <button data-testid={`dispatch-${j.ref}`} onClick={() => navigate(`/jobs/${j.id}`)}
                              className="bg-[#1C1B1A] text-white text-xs font-semibold px-3.5 py-2 rounded-full hover:bg-[#2C2B2A] flex items-center gap-1 transition-colors">
                              Dispatch <ArrowRight className="w-3 h-3" />
                            </button>
                            <button data-testid={`cancel-job-${j.ref}`} onClick={async () => {
                              if (!window.confirm(`Cancel job ${j.ref}?`)) return;
                              try { await api.post(`/jobs/${j.id}/status`, { status: "cancelled" }); toast.success("Job cancelled"); load(); }
                              catch (e) { toast.error(fmtErr(e)); }
                            }} className="border border-[#E8E4D9] text-[#78756E] text-xs font-semibold px-3.5 py-2 rounded-full hover:text-red-600 hover:border-red-200 transition-colors">Cancel</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </section>

                  {d.at_risk.length > 0 && (
                    <section data-testid="at-risk-jobs">
                      <h2 className="text-sm font-bold mb-3 flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> At risk ({d.at_risk.length})</h2>
                      <div className="space-y-2.5">
                        {d.at_risk.map((j) => (
                          <div key={j.id} data-testid={`at-risk-${j.ref}`} className="cr-card !p-3.5 !border-[#1C1B1A] flex items-center justify-between gap-2">
                            <div>
                              <p className="font-mono text-[11px] text-zinc-400">{j.ref}</p>
                              <p className="text-sm font-semibold">{j.pickup?.city} → {j.delivery?.city}</p>
                              <p className="text-[11px] text-zinc-500">
                                {j.status === "open" ? "Pickup date reached — still unassigned" : `Status "${j.status.replace(/_/g, " ")}" past schedule`}
                              </p>
                            </div>
                            <StatusPill status={j.status} />
                          </div>
                        ))}
                      </div>
                    </section>
                  )}
                </div>

                <div className="space-y-4">
                  <div className="rounded-[28px] overflow-hidden border border-[#E8E4D9]" data-testid="company-live-map">
                    <MapView height="320px" jobs={d.active}
                      drivers={(d.driver_locations || []).map((l) => ({ ...l }))}
                      onSelectJob={(id) => navigate(`/jobs/${id}`)} />
                  </div>
                  <section className="cr-card !p-4" data-testid="company-upcoming">
                    <h3 className="text-xs font-bold mb-2">Upcoming pickups ({d.upcoming.length})</h3>
                    {d.upcoming.length === 0 && <p className="text-xs text-zinc-400">None scheduled.</p>}
                    {d.upcoming.slice(0, 6).map((j) => (
                      <div key={j.id} className="text-xs py-1.5 border-b border-zinc-50 last:border-0 flex justify-between">
                        <span><strong>{fdate(j.pickup_date)}</strong> · {j.pickup?.city} → {j.delivery?.city}</span>
                        <StatusPill status={j.status} />
                      </div>
                    ))}
                  </section>
                  {d.fees_pending > 0 && (
                    <p className="text-xs text-[#78756E] cr-card !p-4" data-testid="fees-pending-note">
                      {d.fees_pending} platform fee invoice{d.fees_pending > 1 ? "s" : ""} pending.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}

          {tab === "today" && !d && <p className="text-xs text-[#A09D95] py-20 text-center">Loading…</p>}

          {tab === "book" && <div data-testid="company-book"><BookPage embedded /></div>}
          {tab === "account" && <div data-testid="company-account" className="-mt-6"><AccountPage /></div>}

          {tab === "drivers" && d && (
            <div className="grid lg:grid-cols-[1fr_280px] gap-4" data-testid="drivers-tab">
              <div className="rounded-[28px] overflow-hidden border border-[#E8E4D9]">
                <MapView height="520px" jobs={d.active}
                  drivers={(d.driver_locations || []).map((l) => ({ ...l }))}
                  onSelectJob={(id) => navigate(`/jobs/${id}`)} />
              </div>
              <aside className="cr-card !p-4 h-fit" data-testid="drivers-list">
                <h3 className="text-xs font-bold mb-3 flex items-center">My drivers ({(d.driver_locations || []).length})
                  <InfoTip text="Couriers working on your jobs who are sharing GPS. Green = location updated in the last 15 minutes." /></h3>
                {(d.driver_locations || []).length === 0 && (
                  <p className="text-[11px] text-[#A09D95]">No drivers sharing GPS right now. Couriers appear here when they share location from their dashboard.</p>
                )}
                {(d.driver_locations || []).map((l) => {
                  const online = l.updated_at && (Date.now() - new Date(l.updated_at).getTime()) < 15 * 60 * 1000;
                  const job = (d.active || []).find((j) => j.assigned_courier_id === l.driver_id);
                  return (
                    <div key={l.driver_id} className="flex items-center gap-2.5 py-2.5 border-b border-[#F6F3EA] last:border-0" data-testid={`driver-${l.driver_id}`}>
                      <span className={`w-2 h-2 rounded-full shrink-0 ${online ? "bg-emerald-500" : "bg-[#C9C5B8]"}`} />
                      <div className="min-w-0">
                        <p className="text-xs font-semibold truncate">{l.name || l.driver_name || `Driver ${String(l.driver_id).slice(0, 6)}`}</p>
                        <p className="text-[10px] text-[#A09D95]">{job ? `On ${job.ref} · ${job.status.replace(/_/g, " ")}` : online ? "Online" : `Last seen ${fdate(l.updated_at)}`}</p>
                      </div>
                    </div>
                  );
                })}
              </aside>
            </div>
          )}

          {tab === "deliveries" && <DeliveriesTab />}
          {tab === "invoices" && <JobInvoices />}
          {tab === "metrics" && <MetricsTab />}
          {tab === "team" && <TeamTab />}
          {tab === "addresses" && <AddressBookTab />}
          {tab === "providers" && <ProvidersTab />}
          {tab === "reports" && <ReportsTab />}

          {tab === "fleet" && (
            <div className="max-w-2xl">
              <FleetSection defaultType="van_large" idPrefix="fleet" title="Fleet vehicles" tip="Register your own vehicles for capacity matching." />
            </div>
          )}

          {tab === "api" && (
            <div className="grid lg:grid-cols-2 gap-6">
              <section className="cr-card !p-5" data-testid="api-keys-section">
                <h2 className="text-sm font-bold mb-1 flex items-center">API keys<InfoTip text="Create jobs programmatically: POST /api/v1/jobs with header X-API-Key." /></h2>
                <p className="text-xs text-zinc-500 mb-3 font-mono">POST {process.env.REACT_APP_BACKEND_URL}/api/v1/jobs</p>
                {keys.map((k) => (
                  <div key={k.id} className="flex justify-between items-center py-2 border-b border-zinc-50 text-xs">
                    <span className="font-mono">{k.key}</span>
                    <div className="flex items-center gap-2">
                      <StatusPill status={k.active ? "active" : "suspended"} />
                      {k.active && <button data-testid={`revoke-key-${k.id}`} onClick={async () => { await api.delete(`/apikeys/${k.id}`); api.get("/apikeys").then((r) => setKeys(r.data.keys)); }} className="text-zinc-500 hover:text-black">Revoke</button>}
                    </div>
                  </div>
                ))}
                <button data-testid="create-api-key-btn" onClick={async () => {
                  const { data } = await api.post("/apikeys");
                  toast.success(`New key: ${data.key}`, { duration: 15000 });
                  api.get("/apikeys").then((r) => setKeys(r.data.keys));
                }} className="mt-3 bg-black text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:bg-zinc-800">Generate key</button>
              </section>
              <section className="cr-card !p-5" data-testid="webhooks-section">
                <h2 className="text-sm font-bold mb-1 flex items-center">Webhooks<InfoTip text="We POST job events (assigned, status changes, delivered) to your endpoint as they happen." /></h2>
                {webhooks.map((w) => (
                  <div key={w.id} className="flex justify-between items-center py-2 border-b border-zinc-50 text-xs">
                    <span className="font-mono truncate max-w-[220px]">{w.url}</span>
                    <button data-testid={`delete-hook-${w.id}`} onClick={async () => { await api.delete(`/webhooks/${w.id}`); api.get("/webhooks").then((r) => setWebhooks(r.data.webhooks)); }} className="text-zinc-500 hover:text-black">Remove</button>
                  </div>
                ))}
                <div className="flex gap-2 mt-3">
                  <input data-testid="webhook-url" placeholder="https://your-system.com/hook" value={newHook} onChange={(e) => setNewHook(e.target.value)} className={inputCls} />
                  <button data-testid="add-webhook-btn" onClick={async () => {
                    try { await api.post("/webhooks", { url: newHook, events: ["*"] }); setNewHook(""); toast.success("Webhook added"); api.get("/webhooks").then((r) => setWebhooks(r.data.webhooks)); }
                    catch (e) { toast.error(fmtErr(e)); }
                  }} className="bg-black text-white text-sm font-medium px-4 rounded-lg hover:bg-zinc-800 whitespace-nowrap">Add</button>
                </div>
              </section>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
